import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'

import { isNil } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  ProviderInsightsCompetencyCategory,
  ProviderInsightsSubAreaName,
} from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'
import { ProviderInsightsCompetencyArea } from '@lyrahealth-inc/shared-app-logic/src/models/careInsights/CareInsights'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { InsightsMetricCard, InsightsMetricCardProps } from '../../atoms/insightsMetricCard/InsightsMetricCard'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { Accordion } from '../../molecules/accordion/Accordion'
import { BestAndWorstInsights } from '../../molecules/bestAndWorstInsights/BestAndWorstInsights'
import { CareInsightsRelevantArticlesSubsection } from '../../molecules/careInsightsRelevantArticlesSubsection/CareInsightsRelevantArticlesSubsection'
import { SupervisorSkillsCard } from '../../molecules/supervisorSkillsCard/SupervisorSkillsCard'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface CareInsightsSubsectionProps {
  title: string
  competencyAreaName: ProviderInsightsCompetencyCategory
  aiInsightsCurrentMonth?: ProviderInsightsCompetencyArea | null
  aiInsightsPastMonth?: ProviderInsightsCompetencyArea | null
  getClientInfoFromIds?: (clientIds: { value: string | number }[]) => { name: string; clientId: string }[]
  isLoadingClients?: boolean
  onClientPress?: (clientId: string) => void
  onSkillPress?: (subAreaName: ProviderInsightsSubAreaName) => void
  testIDPrefix?: string
  initiallyExpanded?: boolean
  hasNoAIInsights?: boolean
  isSupervisor?: boolean
  isLoadingCareInsightsMetrics?: boolean
  isLoadingProviderInsights?: boolean
  onSupervisorUsageGuidelinesPress?: () => void
  metrics?: InsightsMetricCardProps[]
  metricsPerRow?: number
  supervisorMetrics?: InsightsMetricCardProps[]
  supervisorMetricsPerRow?: number
}

const Container = styled.View(({ theme }) => ({
  borderTopWidth: '1px',
  borderTopColor: theme.colors.borderDefault,
}))

const ChevronContainer = styled.View<{ isExpanded: boolean }>(({ isExpanded, theme }) => ({
  transform: isExpanded ? 'rotate(-90deg)' : 'rotate(90deg)',
  ...(IS_WEB && { transition: '200ms ease-in-out' }),
  marginTop: theme.spacing['4px'],
}))

const ContentContainer = styled.View(({ theme }) => ({
  marginLeft: theme.spacing['40px'],
  gap: theme.spacing['24px'],
  marginTop: theme.spacing['8px'],
  marginBottom: theme.spacing['24px'],
}))

const MetricsContainer = styled.View(({ theme }) => ({
  marginTop: theme.spacing['16px'],
  flexDirection: theme.breakpoints.isMinWidthTablet ? 'row' : 'column',
  flexWrap: 'wrap',
  gap: theme.spacing['16px'],
}))

export const CareInsightsSubsection: React.FC<CareInsightsSubsectionProps> = ({
  title,
  competencyAreaName,
  aiInsightsCurrentMonth,
  aiInsightsPastMonth,
  getClientInfoFromIds = () => [],
  isLoadingClients,
  onClientPress = () => {},
  onSkillPress,
  testIDPrefix,
  initiallyExpanded = false,
  hasNoAIInsights = false,
  isSupervisor = false,
  isLoadingProviderInsights,
  onSupervisorUsageGuidelinesPress,
  metrics,
  metricsPerRow = 3,
  isLoadingCareInsightsMetrics,
  supervisorMetrics,
  supervisorMetricsPerRow = 2,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme() as ThemeType
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded)

  const { bestAndWorst, subAreas: subAreasCurrentMonth, averageScore } = aiInsightsCurrentMonth ?? {}

  const subAreasPastMonth = aiInsightsPastMonth?.subAreas ?? []
  const sectionAverageScore = averageScore?.value

  const hasBestAndWorstInsights =
    (bestAndWorst?.bestScoringSubArea &&
      bestAndWorst?.bestScoringClientIdsAndSessionDates &&
      bestAndWorst?.bestScoringClientIdsAndSessionDates.length > 0) ||
    (bestAndWorst?.worstScoringSubArea &&
      bestAndWorst?.worstScoringClientIdsAndSessionDates &&
      bestAndWorst?.worstScoringClientIdsAndSessionDates.length > 0)

  const hasNoAIInsightsText = (
    <BodyText
      text={formatMessage({
        defaultMessage:
          'Given the nature of this section, we do not have AI insights to display at this time. Please see related metrics.',
        description: 'Text shown when there are not AI insights to display for a section',
      })}
      testID={tID(`${testIDPrefix ?? 'CareInsightsSubsection'}-noAIInsights`)}
      size={BodyTextSize.DEFAULT}
      color={colors.textSecondary}
    />
  )

  const notEnoughSessionsText = (
    <BodyText
      text={formatMessage({
        defaultMessage: 'Not enough recorded sessions for additional care insights',
        description: 'Text shown when there are not enough recorded sessions for additional care insights',
      })}
      size={BodyTextSize.DEFAULT}
      testID={tID(`${testIDPrefix ?? 'CareInsightsSubsection'}-notEnoughSessions`)}
      color={colors.textSecondary}
    />
  )

  return (
    <Container testID={tID(`${testIDPrefix ?? 'CareInsightsSubsection'}`)}>
      <Accordion
        testIDPrefix={testIDPrefix}
        title={<Title text={title} size={TitleSize.LARGE} />}
        disableSubtitleExpansion
        subtitle={
          hasNoAIInsights ? (
            hasNoAIInsightsText
          ) : (
            <BestAndWorstInsights
              bestAndWorst={bestAndWorst}
              competencyAreaName={competencyAreaName}
              isLoading={(isLoadingClients || isLoadingProviderInsights) ?? false}
              onClientPress={onClientPress}
              onSkillPress={onSkillPress}
              getClientInfoFromIds={getClientInfoFromIds}
              testIDPrefix={testIDPrefix}
            />
          )
        }
        iconComponent={
          <ChevronContainer isExpanded={isExpanded}>
            <ChevronIcon fillColor={colors.textPrimary} size={20} direction={ChevronIconDirection.RIGHT} />
          </ChevronContainer>
        }
        content={
          <ContentContainer>
            {!hasNoAIInsights && !hasBestAndWorstInsights && notEnoughSessionsText}
            {isSupervisor &&
              ((!hasNoAIInsights && subAreasCurrentMonth && subAreasCurrentMonth.length > 0) ||
                (hasNoAIInsights && !isNil(supervisorMetrics))) && (
                <SupervisorSkillsCard
                  currentMonthSubAreas={subAreasCurrentMonth}
                  previousMonthSubAreas={subAreasPastMonth}
                  onUsageGuidelinesPress={onSupervisorUsageGuidelinesPress}
                  isLoadingProviderInsights={isLoadingProviderInsights}
                  supervisorMetrics={supervisorMetrics}
                  supervisorMetricsPerRow={supervisorMetricsPerRow}
                  isLoadingCareInsightsMetrics={isLoadingCareInsightsMetrics}
                  sectionAverageScore={sectionAverageScore}
                />
              )}
            {metrics && metrics.length > 0 && (
              <View>
                <Title
                  text={formatMessage({
                    defaultMessage: 'Related metrics',
                    description: 'Header for related metrics subsection in Care Insights',
                  })}
                  size={TitleSize.MEDIUM}
                />
                <MetricsContainer>
                  {metrics.map((metric) => (
                    <InsightsMetricCard
                      key={metric.testID}
                      {...metric}
                      metricsPerRow={metricsPerRow}
                      isLoading={isLoadingCareInsightsMetrics ?? false}
                    />
                  ))}
                </MetricsContainer>
              </View>
            )}
            {(hasNoAIInsights || hasBestAndWorstInsights) && (
              <CareInsightsRelevantArticlesSubsection
                competencyAreaName={competencyAreaName}
                opportunitySubAreaNames={bestAndWorst?.worstScoringSubArea ? [bestAndWorst?.worstScoringSubArea] : []}
              />
            )}
          </ContentContainer>
        }
        initiallyOpen={initiallyExpanded}
        showGradient={false}
        onAccordianStateChanged={(isOpen) => setIsExpanded(!!isOpen)}
        hideExpansionIcon
        topAlignIcon
      />
    </Container>
  )
}

export default CareInsightsSubsection
